import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIsAuthenticated } from '@azure/msal-react';
import { useHistory } from 'react-router-dom';
import ModalCustomerWelcome from '../common/ModalCustomerWelcome';
import {
  liveVetModalLoginVisibleChanged,
  resetLiveVetAccess,
  saveQuote,
  sendAllPetsFSCLead,
  setCurrentQuoteStep,
  validateDiamondUser,
  validateIfEmailExists,
} from '../../actions';
import {
  formatDate,
  isPaycheck,
} from '../../util';
import {
  AFTER_RATE_TAG,
  QuotingSteps,
  SAVE_QUOTE_TYPE,
} from '../../constants';
import {
  useResponsive,
  useRateSummary,
  useCallbackAfterRate,
  useKameleoonVisitedPageEvent,
  useKameleoonWellnessTest,
  useProp103Features,
} from '../../hooks';
import CardPlanMainHeaderCompany from './CardPlanMainHeaderCompany';
import { EffectiveDateCustom } from '../common/EffectiveDateCustom';
import { PowerUpSection } from './powerups/PowerUpSection';
import { URL_LIVE_VET_YOUR_INFO } from '../../routes';
import { MyPetsContainer } from './myPets/MyPetsContainer';
import DiscountApplied from './yourInfo/DiscountApplied';
import { QuotingFloatingImages } from './QuotingFloatingImages';
import { SaveYourQuoteButton } from './SaveYourQuoteButton';
import { LiveVetCard } from './liveVet/LiveVetCard';
import { PlansSection } from './planSection/PlanSection';
import { WhatFigoCovers } from './WhatFigoCovers';
import { WhatsNotCovered } from './WhatsNotCovered';
import { SeeFullCoverage } from './SeeFullCoverage';
import { useTranslations } from '../../hooks/useTranslations';

import './CardPlanMain.css';
import { IncompleteSelectionError } from './IncompleteSelectionError';
import { QuoteSizeWarning } from './QuoteSizeWarning';

const CardPlanMain = () => {
  const history = useHistory();
  const store = useSelector(({ quoting }) => quoting);
  const dispatch = useDispatch();
  useKameleoonVisitedPageEvent();
  const { t } = useTranslations('quoting');
  const { isMobile, isTablet } = useResponsive();
  useKameleoonWellnessTest();
  const prop103 = useProp103Features();

  const {
    isLoading,
    newCustomer: { email },
    parameters,
    petQuoteSelected,
    quoteSelectedValues,
    sessionInformation: { nopCommerceUser },
  } = store;
  const isAuthenticated = useIsAuthenticated();

  const quoteSelected = quoteSelectedValues
    .find((item) => item.quoteId === petQuoteSelected)
    || { petName: 'Pet Name' };

  let petQuote = null;

  if (store.data) {
    petQuote = store.data.ebPetQuoteResponseList
      .find((item) => item.petQuoteId === petQuoteSelected);
  }

  const [isMounted, setIsMounted] = useState(false);
  const [currentZipCode, setCurrentZipCode] = useState('');
  const { petZipCode } = parameters;

  useEffect(() => {
    if (!isMounted || currentZipCode !== petZipCode) {
      setIsMounted(true);
      dispatch(setCurrentQuoteStep(QuotingSteps.SelectPlan.value));
      setCurrentZipCode(petZipCode);
      dispatch(liveVetModalLoginVisibleChanged(false));
      dispatch(resetLiveVetAccess());
    }
  }, [
    currentZipCode,
    dispatch,
    isMounted,
    petZipCode,
  ]);

  useEffect(() => {
    async function validateCustomerB2C() {
      if (!nopCommerceUser.LoginSuccess && email && !isAuthenticated) {
        const payload = await validateIfEmailExists(email);
        const { userAlreadyExists, ...restData } = payload;

        dispatch(validateDiamondUser({
          customerExistsData: restData,
          email,
          userAlreadyExists,
          validateCustomer: false,
        }));
      }
    }

    validateCustomerB2C();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { getSelectedRate } = useRateSummary();

  useCallbackAfterRate(() => {
    getSelectedRate();
  }, [
    AFTER_RATE_TAG.createRate,
    AFTER_RATE_TAG.addPet,
    AFTER_RATE_TAG.removePet,
  ]);

  const containerCN = useMemo(() => (prop103
    ? 'Card-plan-main-container-prop103'
    : ''), [prop103]);

  useCallbackAfterRate(() => {
    dispatch(sendAllPetsFSCLead());
  }, [
    AFTER_RATE_TAG.updatePet,
  ]);

  useCallbackAfterRate(async () => {
    const {
      customerValidationResponse,
      newCustomer,
      guidData,
      saveQuote: {
        quoteId,
      },
      data,
    } = store;
    const {
      groupCode,
      groupCodeDscr,
      ebPetQuoteResponseList,
      zipCode,
    } = data;
    const cloudClientId = customerValidationResponse?.id || 0;
    const { GuID, EmployerEnrollmentPeriodEBs } = guidData;
    await dispatch(saveQuote({
      cloudClientId,
      ebPetQuoteResponseList,
      EffectiveDate:
        formatDate(EmployerEnrollmentPeriodEBs[0].PolicyEffectiveDate),
      email: newCustomer.email || parameters.email,
      firstName: newCustomer.name || '',
      groupCode,
      groupCodeDscr,
      GuID,
      lastName: newCustomer.lastName || '',
      origin: window.location.origin,
      quoteId,
      quoteSelectedValues,
      QuoteSubId: store.subId,
      quoteType: SAVE_QUOTE_TYPE.auto,
      zipCode,
    }));
    dispatch(sendAllPetsFSCLead());
  }, [AFTER_RATE_TAG.openManualSavedQuote]);

  function getPetName() {
    if (quoteSelected) {
      const petNameFactor = 27;
      const visiblePetName = quoteSelected.petName.length < petNameFactor
        ? quoteSelected.petName
        : `${quoteSelected.petName.slice(0, petNameFactor)}...`;

      if (prop103) {
        return t('prop103.planSectionTitle', { petName: visiblePetName });
      }

      return t('planSectionTitle', { petName: visiblePetName });
    }

    return 'N/A';
  }

  const goToLiveVet = () => {
    history.push(URL_LIVE_VET_YOUR_INFO, { isCustomerLogged: true });
  };

  return (
    <div
      className={'App-card-plan-main-container '
        + 'Card-plan-main-container-elevated'
        + ' App-card-plan-main-container-bg'}
    >
      <ModalCustomerWelcome goTo={goToLiveVet} />

      <div
        className={'Card-plan-main-container-enrollment '
          + `Card-plan-main-container-margin-top ${containerCN}`}
      >
        <section
          aria-label={t('cardPlanHeader')}
          className="Card-plan-header-title-container"
        >
          <CardPlanMainHeaderCompany />
        </section>

        <QuoteSizeWarning />

        <IncompleteSelectionError />

        <h1 className="Card-plan-header-title">
          {getPetName()}
        </h1>

        <PlansSection />

        {isPaycheck ? (
          <span className="Deductible-legend-text">
            Based on 24 pay periods
          </span>
        ) : null}

        <EffectiveDateCustom />

        <DiscountApplied />

        <PowerUpSection
          petQuote={petQuote}
          quoteSelected={quoteSelected}
        />

        <MyPetsContainer />

        {!isLoading
          && (
            <>
              <WhatFigoCovers />

              <WhatsNotCovered />

              <SeeFullCoverage isMobile={isMobile || isTablet} />
            </>
          )}

        <SaveYourQuoteButton />
      </div>

      <QuotingFloatingImages />

      <LiveVetCard email={email} parameters={parameters} />
    </div>
  );
};

export default CardPlanMain;
